import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import PortalModal from '@/components/ui/PortalModal';
import { 
  Users, Search, Edit2, Key, Trash2, X, Save, Loader2, 
  Mail, Shield, AlertCircle, Eye, EyeOff, RefreshCw,
  UserPlus, Phone, Send, UserCheck, Baby, Briefcase,
  GitMerge, ChevronDown, ChevronUp, Check
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';
import AthleteDuplicates from './AthleteDuplicates';

const ROLES = [
  { value: 'coach', label: 'Coach / EPP Staff', color: 'bg-blue-500', icon: Shield },
  { value: 'epp_staff', label: 'EPP Staff', color: 'bg-purple-500', icon: Shield },
  { value: 'partner', label: 'Partner', color: 'bg-indigo-500', icon: Briefcase },
  { value: 'team_manager', label: 'Team Manager', color: 'bg-green-500', icon: Briefcase },
  { value: 'parent', label: 'Parent', color: 'bg-amber-500', icon: Users },
  { value: 'athlete', label: 'Athlete', color: 'bg-pink-500', icon: Baby }
];

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [addUserType, setAddUserType] = useState('team_manager');
  
  // New user form state
  const [newUser, setNewUser] = useState({
    full_name: '',
    email: '',
    phone: '',
    password: '',
    role: 'team_manager'
  });

  // Merge duplicates state
  const [showMergeSection, setShowMergeSection] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [loadingDuplicates, setLoadingDuplicates] = useState(false);
  const [selectedPair, setSelectedPair] = useState(null);
  const [merging, setMerging] = useState(false);
  const [keepUserId, setKeepUserId] = useState(null);
  const [showMergeConfirm, setShowMergeConfirm] = useState(false);
  const [dismissingPair, setDismissingPair] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/users/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users || []);
    } catch (err) {
      console.error('Failed to load users:', err);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const filteredUsers = users.filter(user => {
    const matchesSearch = !searchQuery || 
      user.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = !filterRole || user.role === filterRole;
    return matchesSearch && matchesRole;
  });

  const handleEditUser = (user) => {
    setEditingUser({ 
      ...user,
      full_name: user.full_name || '',
      email: user.email || '',
      phone: user.phone || ''
    });
    setShowEditModal(true);
  };

  const handleResetPassword = (user) => {
    setEditingUser(user);
    setNewPassword('');
    setConfirmPassword('');
    setShowPasswordModal(true);
  };

  const handleAddUser = (type) => {
    setAddUserType(type);
    setNewUser({
      full_name: '',
      email: '',
      phone: '',
      password: '',
      role: type
    });
    setShowAddModal(true);
  };

  const saveUserChanges = async () => {
    if (!editingUser) return;
    
    if (!editingUser.full_name?.trim()) {
      toast.error('Name is required');
      return;
    }
    
    if (!editingUser.email?.trim()) {
      toast.error('Email is required');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const updateData = {
        full_name: editingUser.full_name.trim(),
        email: editingUser.email.trim().toLowerCase(),
        role: editingUser.role
      };
      
      if (editingUser.phone?.trim()) {
        updateData.phone = editingUser.phone.trim();
      }
      
      await axios.patch(`${API}/auth/users/${editingUser.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User updated successfully');
      setShowEditModal(false);
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      console.error('Update error:', err);
      toast.error(err.response?.data?.detail || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const saveNewPassword = async () => {
    if (!editingUser) return;
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/users/${editingUser.id}/reset-password`, {
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Password reset for ${editingUser.full_name}`);
      setShowPasswordModal(false);
      setEditingUser(null);
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSaving(false);
    }
  };

  const createNewUser = async () => {
    if (!newUser.full_name?.trim()) {
      toast.error('Name is required');
      return;
    }
    
    if (!newUser.email?.trim()) {
      toast.error('Email is required');
      return;
    }
    
    if (!newUser.password || newUser.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/create-staff`, {
        full_name: newUser.full_name.trim(),
        email: newUser.email.trim().toLowerCase(),
        phone: newUser.phone?.trim() || '',
        password: newUser.password,
        role: newUser.role
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${getRoleInfo(newUser.role).label} created successfully`);
      setShowAddModal(false);
      setNewUser({ full_name: '', email: '', phone: '', password: '', role: 'team_manager' });
      fetchUsers();
    } catch (err) {
      console.error('Create error:', err);
      toast.error(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const sendInvite = async (user) => {
    setSendingInvite(user.id);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/users/${user.id}/send-invite`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Invitation sent to ${user.email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setSendingInvite(null);
    }
  };

  const deleteUser = async (user) => {
    if (!confirm(`Are you sure you want to delete ${user.full_name}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/auth/users/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const getRoleInfo = (role) => {
    return ROLES.find(r => r.value === role) || { label: role, color: 'bg-gray-500', icon: Users };
  };

  const getAddButtonLabel = (type) => {
    switch(type) {
      case 'team_manager': return 'Add Manager';
      case 'partner': return 'Add Partner';
      case 'parent': return 'Add Parent';
      case 'athlete': return 'Add Athlete';
      case 'epp_staff': return 'Add EPP Staff';
      case 'coach': return 'Add Coach';
      default: return 'Add User';
    }
  };

  // Fetch potential duplicates
  const fetchDuplicates = async () => {
    setLoadingDuplicates(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/users/find-duplicates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDuplicates(response.data.duplicates || []);
    } catch (err) {
      console.error('Failed to find duplicates:', err);
      if (err.response?.status === 403) {
        toast.error('Only EPP Staff can access duplicate detection');
      } else {
        toast.error('Failed to find duplicates');
      }
    } finally {
      setLoadingDuplicates(false);
    }
  };

  // Merge two users
  const mergeUsers = async () => {
    if (!selectedPair || !keepUserId) return;
    
    const deleteUserId = keepUserId === selectedPair.user1.id 
      ? selectedPair.user2.id 
      : selectedPair.user1.id;
    
    setMerging(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/users/merge`, {
        keep_user_id: keepUserId,
        delete_user_id: deleteUserId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Users merged successfully');
      setShowMergeConfirm(false);
      setSelectedPair(null);
      setKeepUserId(null);
      fetchDuplicates();
      fetchUsers();
    } catch (err) {
      console.error('Merge error:', err);
      toast.error(err.response?.data?.detail || 'Failed to merge users');
    } finally {
      setMerging(false);
    }
  };

  const dismissDuplicatePair = async (pair) => {
    const pairKey = `${pair.user1.id}_${pair.user2.id}`;
    setDismissingPair(pairKey);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/users/dismiss-duplicate`, {
        user1_id: pair.user1.id,
        user2_id: pair.user2.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Suggestion dismissed - it won\'t appear again');
      // Remove from local state immediately
      setDuplicates(prev => prev.filter(d => 
        !(d.user1.id === pair.user1.id && d.user2.id === pair.user2.id) &&
        !(d.user1.id === pair.user2.id && d.user2.id === pair.user1.id)
      ));
    } catch (err) {
      console.error('Dismiss error:', err);
      toast.error('Failed to dismiss suggestion');
    } finally {
      setDismissingPair(null);
    }
  };

  const selectPairForMerge = (pair) => {
    setSelectedPair(pair);
    setKeepUserId(null);
    setShowMergeConfirm(true);
  };

  const getMatchReasonBadge = (reasons) => {
    if (!reasons || reasons.length === 0) return null;
    return reasons.map(reason => {
      if (reason === 'same_phone') return { label: 'Same Phone', color: 'bg-red-500' };
      if (reason === 'name_match') return { label: 'Similar Name', color: 'bg-amber-500' };
      if (reason === 'same_domain') return { label: 'Same Domain', color: 'bg-blue-500' };
      return { label: reason, color: 'bg-gray-500' };
    });
  };

  return (
    <div className="space-y-4" data-testid="user-management">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-[#02b5ff]/20 to-[#ff025f]/20 rounded-xl flex items-center justify-center">
            <Users className="h-5 w-5 text-[#02b5ff]" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">User Management</h2>
            <p className="text-xs text-zinc-500">{users.length} total users</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => handleAddUser('team_manager')}
            className="bg-gradient-to-r from-green-500 to-emerald-500 text-white text-sm"
            data-testid="add-manager-btn"
          >
            <Briefcase className="h-4 w-4 mr-1" />
            Add Manager
          </Button>
          <Button
            onClick={() => handleAddUser('parent')}
            className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm"
            data-testid="add-parent-btn"
          >
            <Users className="h-4 w-4 mr-1" />
            Add Parent
          </Button>
          <Button
            onClick={() => handleAddUser('athlete')}
            className="bg-gradient-to-r from-pink-500 to-rose-500 text-white text-sm"
            data-testid="add-athlete-btn"
          >
            <Baby className="h-4 w-4 mr-1" />
            Add Athlete
          </Button>
          <Button
            onClick={() => handleAddUser('epp_staff')}
            variant="outline"
            className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10 text-sm"
            data-testid="add-staff-btn"
          >
            <Shield className="h-4 w-4 mr-1" />
            Add Staff
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchUsers}
            className="text-zinc-400 hover:text-white"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name or email..."
            className="pl-10 bg-zinc-800 border-zinc-700 text-white"
            data-testid="user-search-input"
          />
        </div>
        <select
          value={filterRole}
          onChange={(e) => setFilterRole(e.target.value)}
          className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
          data-testid="user-role-filter"
        >
          <option value="">All Roles</option>
          {ROLES.map(role => (
            <option key={role.value} value={role.value}>{role.label}</option>
          ))}
        </select>
      </div>

      {/* User List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-[#02b5ff]" />
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="text-center py-12 text-zinc-500">
          <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>No users found</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">
          {filteredUsers.map((user) => {
            const roleInfo = getRoleInfo(user.role);
            const RoleIcon = roleInfo.icon;
            return (
              <div
                key={user.id}
                className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700/50 hover:border-zinc-600 transition-all flex items-center justify-between gap-4"
                data-testid={`user-row-${user.id}`}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#02b5ff] to-[#ff025f] flex items-center justify-center text-white font-bold text-sm">
                    {user.full_name?.charAt(0) || '?'}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-white truncate">{user.full_name}</p>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium text-white ${roleInfo.color} flex items-center gap-1`}>
                        <RoleIcon className="h-3 w-3" />
                        {roleInfo.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-zinc-400">
                      <span className="truncate">{user.email}</span>
                      {user.phone && (
                        <span className="flex items-center gap-1 hidden sm:flex">
                          <Phone className="h-3 w-3" />
                          {user.phone}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => sendInvite(user)}
                    disabled={sendingInvite === user.id}
                    className="text-zinc-400 hover:text-emerald-400 hover:bg-emerald-500/10"
                    title="Send invitation email"
                    data-testid={`send-invite-${user.id}`}
                  >
                    {sendingInvite === user.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEditUser(user)}
                    className="text-zinc-400 hover:text-white hover:bg-zinc-700"
                    title="Edit user details"
                    data-testid={`edit-user-${user.id}`}
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleResetPassword(user)}
                    className="text-zinc-400 hover:text-[#02b5ff] hover:bg-[#02b5ff]/10"
                    title="Reset password"
                    data-testid={`reset-password-${user.id}`}
                  >
                    <Key className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteUser(user)}
                    className="text-zinc-400 hover:text-red-500 hover:bg-red-500/10"
                    title="Delete user"
                    data-testid={`delete-user-${user.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add User Modal */}
      <PortalModal isOpen={showAddModal} onClose={() => setShowAddModal(false)} maxWidth="max-w-md">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <UserPlus className="h-5 w-5 text-[#02b5ff]" />
            <h3 className="text-lg font-bold text-white">{getAddButtonLabel(addUserType)}</h3>
          </div>
          <button onClick={() => setShowAddModal(false)} className="text-zinc-500 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="text-sm text-zinc-400 mb-1 block">Full Name *</label>
            <Input
              value={newUser.full_name}
              onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
              placeholder="John Smith"
              className="bg-zinc-800 border-zinc-700 text-white"
              data-testid="new-user-name"
            />
          </div>
          <div>
            <label className="text-sm text-zinc-400 mb-1 block">Email *</label>
            <Input
              type="email"
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              placeholder="john@example.com"
              className="bg-zinc-800 border-zinc-700 text-white"
              data-testid="new-user-email"
            />
          </div>
          <div>
            <label className="text-sm text-zinc-400 mb-1 block">Phone</label>
            <Input
              value={newUser.phone}
              onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
              placeholder="07123456789"
              className="bg-zinc-800 border-zinc-700 text-white"
              data-testid="new-user-phone"
            />
          </div>
          <div>
            <label className="text-sm text-zinc-400 mb-1 block">Role</label>
            <select
              value={newUser.role}
              onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
              data-testid="new-user-role"
            >
              <option value="team_manager">Team Manager</option>
              <option value="partner">Partner</option>
              <option value="parent">Parent</option>
              <option value="athlete">Athlete</option>
              <option value="epp_staff">EPP Staff</option>
              <option value="coach">Coach / EPP Staff</option>
            </select>
          </div>
          <div>
            <label className="text-sm text-zinc-400 mb-1 block">Password *</label>
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                placeholder="Min 6 characters"
                className="bg-zinc-800 border-zinc-700 text-white pr-10"
                data-testid="new-user-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {newUser.password && newUser.password.length < 6 && (
              <p className="text-xs text-amber-400 mt-1">Password must be at least 6 characters</p>
            )}
          </div>
        </div>
        
        <div className="flex gap-3 mt-6">
          <Button
            variant="outline"
            onClick={() => setShowAddModal(false)}
            className="flex-1 border-zinc-700"
          >
            Cancel
          </Button>
          <Button
            onClick={createNewUser}
            disabled={saving || !newUser.full_name || !newUser.email || newUser.password?.length < 6}
            className="flex-1 bg-gradient-to-r from-[#02b5ff] to-[#0090cc] text-white disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
            Create & Send Invite
          </Button>
        </div>
      </PortalModal>

      {/* Edit User Modal */}
      <PortalModal isOpen={showEditModal && !!editingUser} onClose={() => setShowEditModal(false)} maxWidth="max-w-md">
        {editingUser && (
          <>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Edit2 className="h-5 w-5 text-[#02b5ff]" />
                <h3 className="text-lg font-bold text-white">Edit User</h3>
              </div>
              <button onClick={() => setShowEditModal(false)} className="text-zinc-500 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Full Name *</label>
                <Input
                  value={editingUser.full_name}
                  onChange={(e) => setEditingUser({ ...editingUser, full_name: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="edit-user-name"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Email *</label>
                <Input
                  type="email"
                  value={editingUser.email}
                  onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="edit-user-email"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Phone</label>
                <Input
                  value={editingUser.phone || ''}
                  onChange={(e) => setEditingUser({ ...editingUser, phone: e.target.value })}
                  placeholder="07123456789"
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="edit-user-phone"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Role</label>
                <select
                  value={editingUser.role}
                  onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                  data-testid="edit-user-role"
                >
                  <option value="athlete">Athlete</option>
                  <option value="parent">Parent</option>
                  <option value="team_manager">Team Manager</option>
                  <option value="partner">Partner</option>
                  <option value="epp_staff">EPP Staff</option>
                  <option value="coach">Coach / EPP Staff</option>
                </select>
                <p className="text-xs text-zinc-500 mt-1">
                  Changing roles will affect user permissions and dashboard access
                </p>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowEditModal(false)}
                className="flex-1 border-zinc-700"
              >
                Cancel
              </Button>
              <Button
                onClick={saveUserChanges}
                disabled={saving || !editingUser.full_name?.trim() || !editingUser.email?.trim()}
                className="flex-1 bg-gradient-to-r from-[#02b5ff] to-[#0090cc] text-white disabled:opacity-50"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Save Changes
              </Button>
            </div>
          </>
        )}
      </PortalModal>

      {/* Reset Password Modal */}
      <PortalModal isOpen={showPasswordModal && !!editingUser} onClose={() => setShowPasswordModal(false)} maxWidth="max-w-md">
        {editingUser && (
          <>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Key className="h-5 w-5 text-[#02b5ff]" />
                <h3 className="text-lg font-bold text-white">Reset Password</h3>
              </div>
              <button onClick={() => setShowPasswordModal(false)} className="text-zinc-500 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="bg-zinc-800/50 rounded-lg p-3 mb-4">
              <p className="text-sm text-zinc-400">Resetting password for:</p>
              <p className="font-semibold text-white">{editingUser.full_name}</p>
              <p className="text-sm text-zinc-500">{editingUser.email}</p>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">New Password *</label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="bg-zinc-800 border-zinc-700 text-white pr-10"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Confirm Password *</label>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="confirm-password-input"
                />
              </div>
              
              {newPassword && confirmPassword && newPassword !== confirmPassword && (
                <div className="flex items-center gap-2 text-red-400 text-sm">
                  <AlertCircle className="h-4 w-4" />
                  Passwords do not match
                </div>
              )}
              
              {newPassword && newPassword.length < 6 && (
                <div className="flex items-center gap-2 text-amber-400 text-sm">
                  <AlertCircle className="h-4 w-4" />
                  Password must be at least 6 characters
                </div>
              )}
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowPasswordModal(false)}
                className="flex-1 border-zinc-700"
              >
                Cancel
              </Button>
              <Button
                onClick={saveNewPassword}
                disabled={saving || newPassword.length < 6 || newPassword !== confirmPassword}
                className="flex-1 bg-gradient-to-r from-[#02b5ff] to-[#0090cc] text-white disabled:opacity-50"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Key className="h-4 w-4 mr-2" />}
                Reset Password
              </Button>
            </div>
          </>
        )}
      </PortalModal>

      {/* Merge Duplicates Section */}
      <div className="border-t border-zinc-700/50 pt-4 mt-4">
        <button
          onClick={() => {
            setShowMergeSection(!showMergeSection);
            if (!showMergeSection && duplicates.length === 0) {
              fetchDuplicates();
            }
          }}
          className="flex items-center justify-between w-full p-3 bg-zinc-800/50 rounded-xl hover:bg-zinc-800 transition-colors"
          data-testid="merge-duplicates-toggle"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-xl flex items-center justify-center">
              <GitMerge className="h-5 w-5 text-orange-400" />
            </div>
            <div className="text-left">
              <h3 className="font-semibold text-white">Find & Merge Duplicates</h3>
              <p className="text-xs text-zinc-500">Detect and merge duplicate user accounts</p>
            </div>
          </div>
          {showMergeSection ? <ChevronUp className="h-5 w-5 text-zinc-400" /> : <ChevronDown className="h-5 w-5 text-zinc-400" />}
        </button>

        {showMergeSection && (
          <div className="mt-4 space-y-4" data-testid="merge-duplicates-section">
            <div className="flex items-center justify-between">
              <p className="text-sm text-zinc-400">
                {duplicates.length} potential duplicate{duplicates.length !== 1 ? 's' : ''} found
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchDuplicates}
                disabled={loadingDuplicates}
                className="text-zinc-400 hover:text-white"
              >
                {loadingDuplicates ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              </Button>
            </div>

            {loadingDuplicates ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-orange-400" />
              </div>
            ) : duplicates.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">
                <GitMerge className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No duplicate users detected</p>
                <p className="text-xs mt-1">Users are matched by similar names or same phone numbers</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                {duplicates.map((pair, idx) => (
                  <div
                    key={idx}
                    className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700/50 hover:border-orange-500/50 transition-all"
                    data-testid={`duplicate-pair-${idx}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* User 1 */}
                        <div className="bg-zinc-900/50 rounded-lg p-3">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#02b5ff] to-[#ff025f] flex items-center justify-center text-white font-bold text-xs">
                              {pair.user1.full_name?.charAt(0) || '?'}
                            </div>
                            <div>
                              <p className="font-medium text-white text-sm">{pair.user1.full_name}</p>
                              <p className="text-xs text-zinc-500">{pair.user1.email}</p>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            <span className={`px-2 py-0.5 rounded text-xs text-white ${getRoleInfo(pair.user1.role).color}`}>
                              {getRoleInfo(pair.user1.role).label}
                            </span>
                            {pair.user1.phone && (
                              <span className="px-2 py-0.5 rounded text-xs bg-zinc-700 text-zinc-300 flex items-center gap-1">
                                <Phone className="h-3 w-3" />
                                {pair.user1.phone}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* User 2 */}
                        <div className="bg-zinc-900/50 rounded-lg p-3">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#02b5ff] to-[#ff025f] flex items-center justify-center text-white font-bold text-xs">
                              {pair.user2.full_name?.charAt(0) || '?'}
                            </div>
                            <div>
                              <p className="font-medium text-white text-sm">{pair.user2.full_name}</p>
                              <p className="text-xs text-zinc-500">{pair.user2.email}</p>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            <span className={`px-2 py-0.5 rounded text-xs text-white ${getRoleInfo(pair.user2.role).color}`}>
                              {getRoleInfo(pair.user2.role).label}
                            </span>
                            {pair.user2.phone && (
                              <span className="px-2 py-0.5 rounded text-xs bg-zinc-700 text-zinc-300 flex items-center gap-1">
                                <Phone className="h-3 w-3" />
                                {pair.user2.phone}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => selectPairForMerge(pair)}
                          className="bg-gradient-to-r from-orange-500 to-red-500 text-white text-sm"
                          data-testid={`merge-btn-${idx}`}
                        >
                          <GitMerge className="h-4 w-4 mr-1" />
                          Merge
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => dismissDuplicatePair(pair)}
                          disabled={dismissingPair === `${pair.user1.id}_${pair.user2.id}`}
                          className="text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700"
                          title="Dismiss this suggestion"
                          data-testid={`dismiss-btn-${idx}`}
                        >
                          {dismissingPair === `${pair.user1.id}_${pair.user2.id}` ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <X className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Match reasons */}
                    <div className="mt-3 flex flex-wrap gap-2 items-center">
                      <span className="text-xs text-zinc-500">Match reason:</span>
                      {getMatchReasonBadge(pair.match_reasons)?.map((badge, bidx) => (
                        <span key={bidx} className={`px-2 py-0.5 rounded text-xs text-white ${badge.color}`}>
                          {badge.label}
                        </span>
                      ))}
                      <span className="text-xs text-zinc-500 ml-2">
                        Name similarity: {Math.round(pair.similarity.name * 100)}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Athlete / Children Duplicates — extracted to its own component */}
      <AthleteDuplicates />



      {/* Merge Confirmation Modal */}
      {showMergeConfirm && selectedPair && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4" onClick={() => setShowMergeConfirm(false)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-lg p-6 relative z-[10000]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <GitMerge className="h-5 w-5 text-orange-400" />
                <h3 className="text-lg font-bold text-white">Merge Users</h3>
              </div>
              <button onClick={() => setShowMergeConfirm(false)} className="text-zinc-500 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-amber-200 font-medium">This action cannot be undone</p>
                  <p className="text-xs text-amber-300/70 mt-1">
                    All data from the deleted user will be transferred to the kept user. The deleted user will be archived.
                  </p>
                </div>
              </div>
            </div>

            <p className="text-sm text-zinc-400 mb-4">Select which user to keep:</p>

            <div className="space-y-3">
              {/* User 1 Option */}
              <button
                onClick={() => setKeepUserId(selectedPair.user1.id)}
                className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                  keepUserId === selectedPair.user1.id 
                    ? 'border-green-500 bg-green-500/10' 
                    : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-600'
                }`}
                data-testid="keep-user1-btn"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#02b5ff] to-[#ff025f] flex items-center justify-center text-white font-bold">
                      {selectedPair.user1.full_name?.charAt(0) || '?'}
                    </div>
                    <div>
                      <p className="font-semibold text-white">{selectedPair.user1.full_name}</p>
                      <p className="text-sm text-zinc-400">{selectedPair.user1.email}</p>
                      {selectedPair.user1.phone && (
                        <p className="text-xs text-zinc-500 flex items-center gap-1 mt-1">
                          <Phone className="h-3 w-3" />
                          {selectedPair.user1.phone}
                        </p>
                      )}
                    </div>
                  </div>
                  {keepUserId === selectedPair.user1.id && (
                    <Check className="h-6 w-6 text-green-400" />
                  )}
                </div>
              </button>

              {/* User 2 Option */}
              <button
                onClick={() => setKeepUserId(selectedPair.user2.id)}
                className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                  keepUserId === selectedPair.user2.id 
                    ? 'border-green-500 bg-green-500/10' 
                    : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-600'
                }`}
                data-testid="keep-user2-btn"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#02b5ff] to-[#ff025f] flex items-center justify-center text-white font-bold">
                      {selectedPair.user2.full_name?.charAt(0) || '?'}
                    </div>
                    <div>
                      <p className="font-semibold text-white">{selectedPair.user2.full_name}</p>
                      <p className="text-sm text-zinc-400">{selectedPair.user2.email}</p>
                      {selectedPair.user2.phone && (
                        <p className="text-xs text-zinc-500 flex items-center gap-1 mt-1">
                          <Phone className="h-3 w-3" />
                          {selectedPair.user2.phone}
                        </p>
                      )}
                    </div>
                  </div>
                  {keepUserId === selectedPair.user2.id && (
                    <Check className="h-6 w-6 text-green-400" />
                  )}
                </div>
              </button>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowMergeConfirm(false)}
                className="flex-1 border-zinc-700"
              >
                Cancel
              </Button>
              <Button
                onClick={mergeUsers}
                disabled={merging || !keepUserId}
                className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 text-white disabled:opacity-50"
              >
                {merging ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <GitMerge className="h-4 w-4 mr-2" />}
                Confirm Merge
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
